// src/App.js
import React, { useContext, useState, useEffect, Suspense } from 'react';

// Route-level code splitting (2026-09-03): the app shipped as ONE
// 3.27MB bundle — a weak cellular link died mid-download over and over
// (live incident). Each route now loads its own chunk; a chunk that
// fails to fetch (dropped connection, or a deploy replaced the hashed
// file) reloads the page ONCE to pick up a fresh index instead of
// stranding the user on a spinner.
function lazyRetry(importer) {
  return React.lazy(() => importer()
    .then((m) => {
      try { sessionStorage.removeItem('av_chunk_reload'); } catch (_) {}
      return m;
    })
    .catch((err) => {
      try {
        if (!sessionStorage.getItem('av_chunk_reload')) {
          sessionStorage.setItem('av_chunk_reload', '1');
          window.location.reload();
          return new Promise(() => {}); // reload imminent
        }
      } catch (_) {}
      throw err;
    }));
}
const MyProfile = lazyRetry(() => import('./components/MyProfile'));
const MyIntakeForm = lazyRetry(() => import('./components/MyIntakeForm'));
const AdminDashboard = lazyRetry(() => import('./components/AdminDashboard'));
import BootScreen from './components/ui/BootScreen';
import { useFixPlacesDropdownAboveKeyboard } from './hooks/useFixPlacesDropdownAboveKeyboard';
import NotFound from './components/ui/NotFound';


// Google Maps script loader.
//
// Module-level promise: every caller awaits the same script tag so we
// never inject duplicates. Gated to map-using routes (Locations,
// booking address steps) via useGoogleMaps() rather than firing on
// every page load — finding №3 in the launch audit.
let _gmapsPromise = null;
const isProd = process.env.NODE_ENV === 'production';
export const loadGoogleMapsScript = () => {
  if (_gmapsPromise) return _gmapsPromise;
  if (typeof window !== 'undefined' && window.google && window.google.maps) {
    _gmapsPromise = Promise.resolve();
    return _gmapsPromise;
  }
  if (!isProd) console.log('[GoogleMaps] loading Google Maps API script');
  _gmapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!isProd) console.log('[GoogleMaps] script loaded');
      resolve();
    };
    script.onerror = (error) => {
      if (!isProd) console.error('[GoogleMaps] script load failed', error);
      _gmapsPromise = null;
      reject(error);
    };
    document.head.appendChild(script);
  });
  return _gmapsPromise;
};
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { ensurePushSubscription } from './utils/pushSubscribe';
import Header from './components/Navigation/Header';
import HomeGate from './components/HomeGate';
const GiftPage = lazyRetry(() => import('./components/GiftPage'));
const GiftPurchase = lazyRetry(() => import('./components/GiftPurchase'));
const EventSignupPage = lazyRetry(() => import('./components/EventSignupPage'));
const ProviderGifts = lazyRetry(() => import('./components/ProviderGifts'));
const AvailabilityGlance = lazyRetry(() => import('./components/AvailabilityGlance'));
import Landing from './components/Landing';
// Review mount for the landing redesign ("The Intake Binder"). Renders
// for anyone, signed in or not, so Ivan can look at it from any
// session; replaces Landing at "/" only on his go.
import LandingBinder from './components/LandingBinder';
// Review mount for the provider-page cleanup around Ivan's calendar,
// pinned to his own page. A static segment outranks /p/:joinCode.
import ProviderPageNext from './components/ProviderPageNext';
import Login from './components/Login';
const SignUp = lazyRetry(() => import('./components/SignUp'));
const ProfileSetup = lazyRetry(() => import('./components/ProfileSetup'));
const BookingForm = lazyRetry(() => import('./components/BookingForm_updated'));
const ConciergeBookingFlow = lazyRetry(() => import('./components/ConciergeBookingFlow'));
const LogPastAppointmentForm = lazyRetry(() => import('./components/LogPastAppointmentForm'));
const ProviderPackages = lazyRetry(() => import('./components/ProviderPackages'));
const ProviderMemberships = lazyRetry(() => import('./components/ProviderMemberships'));
const PackageDetailPage = lazyRetry(() => import('./components/packages/PackageDetailPage'));
const ClientPackageDetailPage = lazyRetry(() => import('./components/packages/ClientPackageDetailPage'));
const BookingList = lazyRetry(() => import('./components/BookingList'));
const ProviderDashboard = lazyRetry(() => import('./components/ProviderDashboard'));
import ProviderShell from './components/Navigation/ProviderShell';
import ClientShell from './components/Navigation/ClientShell';
import PricesGate from './prices/PricesGate';
const ProviderAvailability = lazyRetry(() => import('./components/ProviderAvailability'));
const ProviderAppointments = lazyRetry(() => import('./components/ProviderAppointments'));
const ProviderEvents = lazyRetry(() => import('./components/ProviderEvents'));
// ProviderClients removed — Roster absorbs its surfaces. The old
// /provider/clients{,/:id} routes still redirect to the Roster
// equivalents for any bookmarked URLs.
const ProviderRoster = lazyRetry(() => import('./components/ProviderRoster'));
const ProviderRequests = lazyRetry(() => import('./components/ProviderRequests'));
const ProviderRelationship = lazyRetry(() => import('./components/ProviderRelationship'));
const BookingFork = lazyRetry(() => import('./components/BookingFork'));
// ProviderClientDetails removed — its surfaces live on /provider/roster/:id now.
const ProviderLocations = lazyRetry(() => import('./components/ProviderLocations'));
const OutreachComposerPage = lazyRetry(() => import('./outreach'));
const PromotionsPage = lazyRetry(() => import('./components/promotions/PromotionsPage'));
const ProviderNotes = lazyRetry(() => import('./components/ProviderNotes'));
const ProviderMessages = lazyRetry(() => import('./components/ProviderMessages'));
const ProviderAssistant = lazyRetry(() => import('./assistant/ProviderAssistant'));
const ProviderServices = lazyRetry(() => import('./components/ProviderServices'));
const PricesPage = lazyRetry(() => import('./components/prices/PricesPage'));
const ProviderSettings = lazyRetry(() => import('./components/ProviderSettings'));
const ProviderBilling = lazyRetry(() => import('./components/ProviderBilling'));
const ReceiptView = lazyRetry(() => import('./components/ReceiptView'));
const ProviderWizard = lazyRetry(() => import('./components/ProviderWizard'));
const ForgotPassword = lazyRetry(() => import('./components/ForgotPassword'));
const ResetPassword = lazyRetry(() => import('./components/ResetPassword'));
const ProviderSelection = lazyRetry(() => import('./components/ProviderSelection'));
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import InstallPromptBanner from './components/InstallPromptBanner';
import { useVersionCheck } from './utils/useVersionCheck';
import { register as registerServiceWorker, applyUpdate as applyServiceWorkerUpdate } from './serviceWorkerRegistration';
const ProviderAssignmentRequests = lazyRetry(() => import('./components/ProviderAssignmentRequests'));
const WeeklyTemplateEditor = lazyRetry(() => import('./components/WeeklyTemplateEditor'));
const AppointmentDetail = lazyRetry(() => import('./components/AppointmentDetail'));
const NotificationsInbox = lazyRetry(() => import('./components/NotificationsInbox'));
const ClientSettings = lazyRetry(() => import('./components/ClientSettings'));
const PublicProviderProfile = lazyRetry(() => import('./components/PublicProviderProfile'));
const PublicBookRoute = lazyRetry(() => import('./components/PublicBookRoute'));
const ClientJoinWizard = lazyRetry(() => import('./components/ClientJoinWizard'));
const RecordsReleaseDoc = lazyRetry(() => import('./components/RecordsReleaseDoc'));
const ReportsPage = lazyRetry(() => import('./components/ExpensesReport'));
const ClaimAccount = lazyRetry(() => import('./components/ClaimAccount'));
const Packages = lazyRetry(() => import('./components/Packages'));
const MyPackages = lazyRetry(() => import('./components/MyPackages'));
const MyMembership = lazyRetry(() => import('./components/MyMembership'));

// Picks the provider wizard or the legacy client ProfileSetup based on
// the authenticated user's accountType. Providers get a one-screen-per-
// question wizard; clients keep the existing form (their fields are
// different and the form is short enough).
const ProfileSetupRouter = () => {
  const { user } = useContext(AuthContext);
  if (user?.accountType === 'PROVIDER') return <ProviderWizard />;
  // Clients NEVER see profile-setup (Ivan, 2026-08-29): every client
  // door (booking wizard, join wizard, provider-created) captures
  // name/phone/consents already, and the intake owns preferences. The
  // legacy ProfileSetup form is provider-funnel remnant for them —
  // Seanathan hit it after his first sign-in because booking-created
  // accounts carry registrationStep 1.
  return <Navigate to="/" replace />;
};

// Backstop for stale links to /appointments/:id. The provider-canonical
// URL is /provider/appointments/:id (wraps in ProviderShell for the
// desktop sidebar). Any stray provider hit to the bare path gets a soft
// redirect to the canonical URL. Clients keep the bare path — they
// don't have a provider sidebar.
const AppointmentDetailRoute = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  if (user?.accountType === 'PROVIDER') {
    return <Navigate to={`/provider/appointments/${id}`} replace />;
  }
  return <AppointmentDetail />;
};

// /notifications serves both roles. Providers get it inside
// ProviderShell (the legacy top-nav design is retired on provider
// desktop — Header.js hides itself at lg+ for this case); clients keep
// the bare page under the standard Header.
const NotificationsRoute = () => {
  const { user } = useContext(AuthContext);
  if (user?.accountType === 'PROVIDER') {
    return <ProviderShell active="notifications"><NotificationsInbox /></ProviderShell>;
  }
  return <NotificationsInbox />;
};

const RegistrationProtectedRoute = ({ children, requiredStep }) => {
  const { user } = useContext(AuthContext);


  const currentStep = user?.registrationStep || 1;
    if (currentStep < requiredStep) {
      // Clients skip the registration funnel entirely — their doors
      // capture everything at creation (2026-08-29).
      if (user?.accountType !== 'PROVIDER') return children;
      if (currentStep === 1) return <Navigate to="/profile-setup" />;
      if (currentStep === 2) {
        // For providers, skip preferences and go to dashboard
        if (user?.accountType === 'PROVIDER') {
          return <Navigate to="/dashboard" />;
        } else {
          // For clients, redirect to provider selection instead of preferences
          return <Navigate to="/provider-selection" />;
        }
      }
    }

  return children;
};



// Subscription access gate for providers. Reads the subscription
// snapshot the server attaches to the User object (providerProfile.
// subscriptionStatus + subscriptionGrandfathered). 'active' / 'trialing'
// or grandfathered = full access. Anything else routes to /provider/
// billing until they subscribe. SUPER_ADMIN bypasses.
const hasActiveProviderAccess = (user) => {
  if (!user || user.accountType !== 'PROVIDER') return true;
  if (user.accountType === 'SUPER_ADMIN') return true;
  const p = user.providerProfile || {};
  if (p.subscriptionGrandfathered) return true;
  return ['active', 'trialing'].includes(p.subscriptionStatus);
};

// Protected Route Components
//   providerOnly: redirect non-providers to /
//   requireSubscription (default true): providers without active access
//     are routed to /provider/billing. Set false on the billing page
//     itself to avoid an infinite redirect.
const ProtectedRoute = ({ children, providerOnly = false, requireSubscription = true }) => {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return <BootScreen />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (providerOnly && user.accountType !== 'PROVIDER') {
    return <Navigate to="/" />;
  }

  // A provider whose setup wizard never completed (registrationStep < 3)
  // has no name, menu, or join code — letting them wander the provider
  // app half-configured produces exactly the confusion Regina hit
  // (2026-08-21: wizard Finish 500'd, she rebuilt her schedule by hand).
  // Route them back to the wizard; it resumes from their saved answers.
  if (user.accountType === 'PROVIDER' && (user.registrationStep || 1) < 3) {
    return <Navigate to="/profile-setup" />;
  }

  if (requireSubscription && user.accountType === 'PROVIDER' && !hasActiveProviderAccess(user)) {
    return <Navigate to="/provider/billing" />;
  }

  return children;
};

// Soft-redirect for old /provider/clients/:clientId deep links into
// the matching Roster relationship route. Keeps bookmarks alive.
const ClientToRoster = () => {
  const { clientId } = useParams();
  return <Navigate to={`/provider/roster/${clientId}`} replace />;
};

function App() {
  const { user, loading } = useContext(AuthContext);
  // Heal push on open (2026-09-02): a granted-but-dead subscription
  // re-registers silently (pushSubscribe.ensurePushSubscription) —
  // browsers rotate endpoints and the server retires them after three
  // 410s, and until now nothing ever re-subscribed. Keyed on identity
  // so login triggers it too; never prompts.
  useEffect(() => {
    if (user?._id) ensurePushSubscription();
  }, [user?._id]);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  // Force Google Places .pac-container dropdown ABOVE the input on
  // mobile — otherwise it renders below and gets hidden by the
  // on-screen keyboard. See hook file for full explanation.
  useFixPlacesDropdownAboveKeyboard();
  const { updateAvailable } = useVersionCheck();
  // Service-worker update flow runs alongside the version-poll one. SW
  // detects new builds via its own install lifecycle (more reliable
  // than polling), but the poll is kept as a fallback for users on
  // browsers without SW support or before the SW has registered.
  const [swRegistration, setSwRegistration] = useState(null);
  const swUpdateAvailable = swRegistration !== null;

  // Maps loader is gated to map-using routes (useGoogleMaps in
  // Locations / booking address steps). No longer auto-fired on every
  // page load — see findings №3.
  useEffect(() => { setGoogleMapsLoaded(false); }, []);

  useEffect(() => {
    registerServiceWorker({
      onUpdate: (registration) => setSwRegistration(registration),
    });
  }, []);

  const handleBannerReload = () => {
    if (swRegistration) {
      applyServiceWorkerUpdate(swRegistration);
      return;
    }
    window.location.reload();
  };

  if (loading) {
    return <BootScreen />;
  }

  return (
    <div className="App min-h-screen" style={{ background: 'var(--bg)' }}>
      <Header />
      <UpdateAvailableBanner visible={updateAvailable || swUpdateAvailable} onReload={handleBannerReload} />
      <InstallPromptBanner />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:max-w-none lg:px-0 lg:py-0">
        <Suspense fallback={<BootScreen />}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={user ? <Navigate to="/" /> : <Login />}
          />
          <Route
            path="/signup"
            element={user ? <Navigate to="/profile-setup" /> : <SignUp />}
          />
          <Route
            path="/forgot-password"
            element={user ? <Navigate to="/" /> : <ForgotPassword />}
          />
          <Route
            path="/reset-password/:token"
            element={user ? <Navigate to="/" /> : <ResetPassword />}
          />
          <Route path="/p/bookingpagenew" element={<ProviderPageNext joinCode="ivan26" />} />
          <Route
            path="/p/:joinCode"
            element={<PublicProviderProfile />}
          />
          {/* /p/:joinCode/book — the anon-flow entry point that
              DaySheet's Continue routes to. Signed-in matched clients
              are auto-redirected inside PublicBookRoute → /book so
              they land on /api/bookings (not the managed-client path).
              Step 6 consolidation of the wizard. */}
          <Route
            path="/p/:joinCode/book"
            element={<PublicBookRoute googleMapsLoaded={googleMapsLoaded} />}
          />
          {/* /p/:joinCode/join — provider-anchored, booking-free client
              signup. PUBLIC with no user-ternary wrapper: verify sets
              the context user mid-wizard, and a wrapper would unmount
              the wizard at that exact moment. The wizard owns its
              signed-in states internally. */}
          <Route path="/p/:joinCode/join" element={<ClientJoinWizard />} />
          {/* /p/:joinCode/e/:code — an event's QR sign-up sheet. Public;
              writes one EventSignup line, never an account. */}
          <Route path="/p/:joinCode/e/:code" element={<EventSignupPage />} />

          {/* Released session records — standalone printable document.
              Server enforces the two-reader rule (releasing provider +
              the client the notes are about). */}
          <Route
            path="/records/:releaseId"
            element={
              <ProtectedRoute>
                <RecordsReleaseDoc />
              </ProtectedRoute>
            }
          />
          {/* Claim flow — public, intentionally accessible while logged in too
              in case the managed client follows the link from a device where
              the provider (or someone else) already has a session. The server
              treats each request standalone — token is the auth for this flow. */}
          <Route
            path="/claim/:token"
            element={<ClaimAccount />}
          />
          {/* Gift certificates — both public by design. The claim code
              IS the gift (transferable); the purchase page is three
              fields and a Stripe redirect. */}
          <Route path="/g/:code" element={<GiftPage />} />
          <Route path="/p/:joinCode/gift" element={<GiftPurchase />} />

          {/* Receipt views — public when accessed via emailed token link
              (anonymous client opens it from their inbox), authenticated
              when opened from inside the app. The component handles the
              two modes itself. Header is suppressed via Header.js. */}
          <Route
            path="/receipt/b/:id"
            element={<ReceiptView kind="booking" />}
          />
          <Route
            path="/receipt/p/:id"
            element={<ReceiptView kind="package" />}
          />

          {/* Registration Flow Routes. Providers and clients diverge
              at profile-setup: providers get the wizard
              (one-screen-per-question, atelier-styled), clients keep
              the legacy ProfileSetup form (different shape, different
              questions). */}
          <Route
            path="/profile-setup"
            element={
              <RegistrationProtectedRoute requiredStep={1}>
                <ProfileSetupRouter />
              </RegistrationProtectedRoute>
            }
          />

          {/* /treatment-preferences and /client-preferences retired
              2026-08-27 — pressure/focus/avoid live in the health
              intake (/my-intake); every menu door was swept the same
              day. */}

          <Route
            path="/provider-selection"
            element={
              <RegistrationProtectedRoute requiredStep={2}>
                <ProviderSelection />
              </RegistrationProtectedRoute>
            }
          />

          {/* Protected Routes (require completed registration) */}
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute>
                <ClientShell active="profile"><MyProfile /></ClientShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/client/settings"
            element={
              <ProtectedRoute>
                <ClientShell active="settings"><ClientSettings /></ClientShell>
              </ProtectedRoute>
            }
          />

          {/* Mission control — operator analytics, isAdmin only, standalone
              page reached by URL (never auto-routed). */}
          <Route
            path="/admin"
            element={user?.isAdmin ? <AdminDashboard /> : <Navigate to="/" replace />}
          />
          <Route
            path="/my-intake"
            element={
              <ProtectedRoute>
                <ClientShell active="intake"><MyIntakeForm /></ClientShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/book"
            element={
              <ProtectedRoute>
                {/* Signed-in CLIENTS get the concierge (2026-09-01);
                    providers keep the full wizard (on-behalf, override,
                    log-past-session — none of that is client-shaped). */}
                {user?.accountType === 'CLIENT'
                  ? <ConciergeBookingFlow clientMode />
                  : window.location.search.includes('classic')
                    ? <ClientShell active="book"><BookingForm googleMapsLoaded={googleMapsLoaded} /></ClientShell>
                    : <ConciergeBookingFlow providerMode />}
              </ProtectedRoute>
            }
          />
          {/* Booking fork (Phase 2, preview-only) — "Someone we know"
              vs "Someone new". The known-client path routes back to
              /book?clientId=<id>; new clients route straight to /book.
              Non-preview hits redirect to /book so the existing flow
              is untouched in production. */}
          <Route
            path="/book/fork"
            element={
              <ProtectedRoute providerOnly>
                <BookingFork />
              </ProtectedRoute>
            }
          />

          <Route
            path="/availability"
            element={
              <ProtectedRoute>
                <ClientShell active="provider-calendar"><AvailabilityGlance /></ClientShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-bookings"
            element={
              <ProtectedRoute>
                <ClientShell active="my-bookings"><BookingList /></ClientShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/packages"
            element={
              <ProtectedRoute>
                <ClientShell active="buy-packages"><Packages /></ClientShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-packages"
            element={
              <ProtectedRoute>
                <ClientShell active="my-packages"><MyPackages /></ClientShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-membership"
            element={
              <ProtectedRoute>
                <ClientShell active="my-membership"><MyMembership /></ClientShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-packages/:id"
            element={
              <ProtectedRoute>
                <ClientShell active="my-packages"><ClientPackageDetailPage /></ClientShell>
              </ProtectedRoute>
            }
          />

          {/* Provider Routes
              Desktop chrome wrap: ProviderShell renders a sidebar +
              topbar at lg+ around the page content. At <lg it renders
              nothing and the legacy Navigation/Header handles chrome. */}
          <Route
            path="/provider"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="dashboard">
                  <ProviderDashboard />
                </ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/availability"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="availability"><ProviderAvailability /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/requests"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="requests"><ProviderRequests /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/schedule-template"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="weekly-hours"><WeeklyTemplateEditor /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/locations"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="locations"><ProviderLocations /></ProviderShell>
              </ProtectedRoute>
            }
          />
          {/* Old URL — Static Locations got merged into the unified
              Locations page. Keep a redirect so any bookmarks still work. */}
          <Route
            path="/provider/static-locations"
            element={<Navigate to="/provider/locations" replace />}
          />
          <Route
            path="/provider/outreach"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="outreach"><OutreachComposerPage /></ProviderShell>
              </ProtectedRoute>
            }
          />
          {/* The outreach wizard (2026-10-08): full screen, no shell, like /book. */}
          <Route
            path="/provider/outreach/new"
            element={
              <ProtectedRoute providerOnly>
                <OutreachComposerPage wizard />
              </ProtectedRoute>
            }
          />
          {/* Promotions on their own page (2026-10-07); the editor used to sit
              under the Outreach page. */}
          <Route
            path="/provider/promotions"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="promotions"><PromotionsPage /></ProviderShell>
              </ProtectedRoute>
            }
          />
          {/* SOAP notes across every client (2026-10-23); a note used to be
              reachable only from a client's profile or its appointment. */}
          <Route
            path="/provider/notes"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="notes"><ProviderNotes /></ProviderShell>
              </ProtectedRoute>
            }
          />
          {/* The therapist's own assistant (2026-10-25): their day,
              requests, clients and money; changes as cards they tap. In
              the nav only when it's on for the practice. */}
          <Route
            path="/provider/assistant"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="assistant"><ProviderAssistant /></ProviderShell>
              </ProtectedRoute>
            }
          />
          {/* Private messages from the booking assistant (2026-10-24):
              reached from the inbox notification, not the nav. */}
          {['/provider/messages', '/provider/messages/:id'].map((p) => (
            <Route
              key={p}
              path={p}
              element={
                <ProtectedRoute providerOnly>
                  <ProviderShell active="messages"><ProviderMessages /></ProviderShell>
                </ProtectedRoute>
              }
            />
          ))}
          {/* Text a group (2026-09-28) and the composer review route
              (2026-09-30) folded into the letter on 2026-10-01. */}
          <Route path="/provider/campaigns" element={<Navigate to="/provider/outreach" replace />} />
          <Route path="/provider/composer" element={<Navigate to="/provider/outreach" replace />} />
          {/* Legacy URL — keep redirecting so any bookmarks or
              in-flight clients land in the right place. */}
          <Route
            path="/provider/weekly-outreach"
            element={<Navigate to="/provider/outreach" replace />}
          />
          {/* /provider/gap-fill folded into the Outreach page's "Fill gaps"
              section per the design handoff. Old bookmarks redirect. */}
          <Route
            path="/provider/gap-fill"
            element={<Navigate to="/provider/outreach" replace />}
          />
          <Route
            path="/provider/services"
            element={
              <ProtectedRoute providerOnly>
                <PricesGate want="services">
                  <ProviderShell active="services"><ProviderServices /></ProviderShell>
                </PricesGate>
              </ProtectedRoute>
            }
          />
          {/* The price board (2026-10-30): one board for every price, on
              for providers listed in PRICE_BOARD_PROVIDERS. */}
          <Route
            path="/provider/prices"
            element={
              <ProtectedRoute providerOnly>
                <PricesGate want="prices">
                  <ProviderShell active="prices"><PricesPage /></ProviderShell>
                </PricesGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/appointments"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="appointments"><ProviderAppointments /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/events"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="events"><ProviderEvents /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/log-past-appointment"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="appointments"><LogPastAppointmentForm /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/gifts"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="gifts"><ProviderGifts /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/packages"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="packages"><ProviderPackages /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/packages/:id"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="packages"><PackageDetailPage /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/memberships"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="memberships"><ProviderMemberships /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/appointments/:id"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="appointments"><AppointmentDetail /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/appointments/:id"
            element={
              <ProtectedRoute>
                <AppointmentDetailRoute />
              </ProtectedRoute>
            }
          />
          {/* Notifications inbox — shared by providers and clients. The
              header bell on each shell deep-links here; tapping an item
              navigates to its source page. */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsRoute />
              </ProtectedRoute>
            }
          />
          {/* The Roster — relationship-first view of every regular.
              Replaces /provider/clients outright. Old deep links to
              /provider/clients{,/:id} soft-redirect into the matching
              Roster surface so bookmarks stop nowhere. */}
          <Route
            path="/provider/roster"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="clients"><ProviderRoster /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/roster/:clientId"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="clients"><ProviderRelationship /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route path="/provider/clients" element={<Navigate to="/provider/roster" replace />} />
          <Route path="/provider/clients/:clientId" element={<ClientToRoster />} />

          <Route
            path="/provider/assignment-requests"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="clients"><ProviderAssignmentRequests /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/settings"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell><ProviderSettings /></ProviderShell>
              </ProtectedRoute>
            }
          />
          {/* requireSubscription={false} — providers without a sub MUST
              be able to reach this page to subscribe. Without the opt-
              out, the gate in ProtectedRoute would redirect them here
              from itself in a loop. */}
          <Route
            path="/provider/billing"
            element={
              <ProtectedRoute providerOnly requireSubscription={false}>
                <ProviderShell active="billing"><ProviderBilling /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/reports"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="reports"><ReportsPage /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/mileage"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="reports"><ReportsPage /></ProviderShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider/expenses"
            element={
              <ProtectedRoute providerOnly>
                <ProviderShell active="reports"><ReportsPage /></ProviderShell>
              </ProtectedRoute>
            }
          />
          {/* Root route — HomeGate handles the auth-branching so a
              logged-in client's dashboard is CONSOLIDATED at
              /p/:joinCode instead of duplicated here. See
              HomeGate.js for the full branch table. */}
          <Route path="/" element={<HomeGate />} />
          <Route path="/test-landing" element={<LandingBinder />} />

          {/* Catch all route — on-voice 404, not a silent redirect. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default App;
